import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useI18n } from "../lib/i18n";
import { useAuth, API } from "../lib/auth";
import { Sparkles, Loader2, ArrowLeft } from "lucide-react";
import { CONTRACT_TYPES as CONTRACTS, SECTORS } from "../lib/constants";
import { CATEGORIES } from "../lib/seoTaxonomy";

export default function PostJobPage() {
  const { t, lang } = useI18n();
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "", location: "", contract_type: "Full-time", sector: "Tech", category: "",
    salary_min: "", salary_max: "", description: "", requirements: "", benefits: "",
    application_url: "",
    eligibility_type: "authorised_only",
    enforce_eligibility: true,
  });
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const aiGenerate = async () => {
    if (!form.title) { toast.error("Veuillez saisir un titre"); return; }
    setAiLoading(true);
    try {
      const r = await axios.post(`${API}/employer/jobs/ai-describe`, {
        title: form.title, sector: form.sector, contract_type: form.contract_type, location: form.location, language: lang,
      }, { headers: authHeader });
      setForm(prev => ({ ...prev, description: r.data.description }));
      toast.success("Description générée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    setAiLoading(false);
  };

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const payload = {
        ...form,
        category: form.category || null,
        salary_min: form.salary_min ? Number(form.salary_min) : null,
        salary_max: form.salary_max ? Number(form.salary_max) : null,
        application_url: form.application_url?.trim() || null,
        eligibility_type: form.eligibility_type || "authorised_only",
        enforce_eligibility: !!form.enforce_eligibility,
      };
      await axios.post(`${API}/employer/jobs`, payload, { headers: authHeader });
      toast.success("Offre publiée !");
      navigate("/employer");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]" data-testid="post-job-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button onClick={() => navigate("/employer")} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4">
          <ArrowLeft className="h-4 w-4" /> Retour
        </button>
        <h1 className="font-display text-3xl font-bold text-slate-900 mb-6">{t("employer.post")}</h1>
        <Card className="p-6">
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label>{t("employer.title")}</Label>
              <Input data-testid="pj-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("employer.location")}</Label>
                <Input data-testid="pj-location" required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </div>
              <div>
                <Label>{t("employer.contract")}</Label>
                <Select value={form.contract_type} onValueChange={(v) => setForm({ ...form, contract_type: v })}>
                  <SelectTrigger data-testid="pj-contract"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONTRACTS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>{t("employer.sector")}</Label>
                <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v })}>
                  <SelectTrigger data-testid="pj-sector"><SelectValue /></SelectTrigger>
                  <SelectContent>{SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>{t("employer.salaryMin")}</Label><Input data-testid="pj-salmin" type="number" value={form.salary_min} onChange={(e) => setForm({ ...form, salary_min: e.target.value })} /></div>
              <div><Label>{t("employer.salaryMax")}</Label><Input data-testid="pj-salmax" type="number" value={form.salary_max} onChange={(e) => setForm({ ...form, salary_max: e.target.value })} /></div>
            </div>
            <div>
              <Label>{lang === "fr" ? "Catégorie" : "Category"}</Label>
              <Select value={form.category || "none"} onValueChange={(v) => setForm({ ...form, category: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="pj-category"><SelectValue placeholder={lang === "fr" ? "Choisissez une catégorie…" : "Pick a category…"} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{lang === "fr" ? "— Aucune —" : "— None —"}</SelectItem>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.slug} value={c.slug} data-testid={`pj-category-${c.slug}`}>
                      {lang === "fr" ? c.label_fr : c.label_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                {lang === "fr"
                  ? "Aide les candidats à trouver votre offre depuis la page catégorie dédiée (ex. /jobs/category/restauration)."
                  : "Helps candidates find your role via the dedicated category page (e.g. /jobs/category/restauration)."}
              </p>
            </div>
            {/* Applicant Eligibility */}
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid="pj-eligibility-section">
              <div>
                <div className="text-sm font-semibold text-slate-900">{lang === "fr" ? "Éligibilité des candidats" : "Applicant Eligibility"}</div>
                <p className="text-[11px] text-slate-500">{lang === "fr" ? "Qui peut postuler à ce poste ?" : "Who can apply?"}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {[
                  { v: "mauritius_only", l: lang === "fr" ? "Maurice / Rodrigues uniquement" : "Mauritius / Rodrigues only" },
                  { v: "authorised_only", l: lang === "fr" ? "Candidats déjà autorisés à travailler à Maurice" : "Already authorised to work in Mauritius" },
                  { v: "international", l: lang === "fr" ? "Candidats internationaux acceptés" : "International applicants accepted" },
                ].map((o) => (
                  <button
                    type="button"
                    key={o.v}
                    onClick={() => setForm({ ...form, eligibility_type: o.v })}
                    data-testid={`pj-elig-${o.v}`}
                    className={`text-xs h-10 rounded-lg border px-3 text-left transition ${form.eligibility_type === o.v ? "bg-white border-[#1A8FE3] text-[#1A8FE3] ring-1 ring-[#1A8FE3]" : "bg-white border-slate-200 text-slate-700 hover:border-[#1A8FE3]"}`}
                  >{o.l}</button>
                ))}
              </div>
              <label className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  checked={!!form.enforce_eligibility}
                  onChange={(e) => setForm({ ...form, enforce_eligibility: e.target.checked })}
                  data-testid="pj-enforce"
                  className="h-4 w-4 accent-[#1A8FE3]"
                />
                <span className="text-xs text-slate-700">{lang === "fr" ? "Empêcher les candidats non éligibles de postuler" : "Prevent ineligible applicants from applying"}</span>
              </label>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>{t("employer.description")}</Label>
                <Button type="button" size="sm" variant="outline" onClick={aiGenerate} disabled={aiLoading} data-testid="pj-ai-generate" className="rounded-full border-[#1A8FE3] text-[#1A8FE3] hover:bg-sky-50">
                  {aiLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
                  {t("employer.aiGenerate")}
                </Button>
              </div>
              <Textarea data-testid="pj-description" required rows={10} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <Label>{t("employer.requirements")}</Label>
              <Textarea data-testid="pj-requirements" rows={4} value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} />
            </div>
            <div>
              <Label>{t("employer.benefits")}</Label>
              <Textarea data-testid="pj-benefits" rows={3} value={form.benefits} onChange={(e) => setForm({ ...form, benefits: e.target.value })} />
            </div>
            <div>
              <Label>{lang === "fr" ? "Lien de candidature externe (optionnel)" : "External apply URL (optional)"}</Label>
              <Input
                data-testid="pj-application-url"
                type="url"
                placeholder="https://yourcompany.com/careers/apply/123"
                value={form.application_url}
                onChange={(e) => setForm({ ...form, application_url: e.target.value })}
              />
              <p className="text-[11px] text-slate-500 mt-1">
                {lang === "fr"
                  ? "Si renseigné, le bouton « Postuler » redirigera les candidats vers ce lien au lieu d'ouvrir le formulaire interne."
                  : "If set, the Apply button will redirect candidates to this URL instead of opening the in-platform form."}
              </p>
            </div>
            <Button type="submit" disabled={loading} data-testid="pj-submit" className="w-full bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full h-12">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("employer.create")}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
